import {ConfigObjectType, ENABLE_PH_REC, LightLayoutNodeFCProps, MaximizeButton, MinimizeRightButton} from '@wallaby-ui/core';
import {
  AlertEpisodePanel,
  AlertEpisodePanelDto,
  LightFilterTabDef,
  LightFilterTabs,
  MerieSchema,
  RiskFactorLabelEnum,
  RiskFactorTypeEnum,
  TableSorting,
  useFormatters,
} from '@wallaby-ui/document';
import React, {useCallback, useEffect, useState} from 'react';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {HospitalizationRiskFactorsV2} from './HospitalizationDetailRiskFactorsV2';
import {HospitalizationPhRecommendationPanelV2} from './HospitalizationPhRecommendationPanelV2';
import {RIGHT_TAB_ALERTS, RIGHT_TAB_PH_RECOMMENDATION, RIGHT_TAB_RISK_FACTORS} from './hospitalizationRightTabs';
import {alertEpisodeHref} from '../alerts/alertEpisodeHref';
import {useMerieAlertCriteriaRenderer} from '../alerts/MerieAlertCriteriaChips';

export const DEFAULT_RISK_FACTORS_SORTING: TableSorting = {
  field: 'date',
  direction: 'desc',
};
export type HospitalizationRightPaneProps = {
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
  storageIdPrefix: string;
  config: ConfigObjectType;
  hospitalizationId: string;
  defaultDepartmentId: number | null;
  riskFactorToRiskFactorLabel: Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
  patientCardLoading: boolean;
  riskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  riskFactorsEtag: string | undefined;
  oldRiskFactorIds: Set<number>;
  reloadRiskFactors: () => void;
  setTimelineDefaultScroll: (v: number) => void;
  pharmaRecommendationsLoading: boolean;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
  pharmaRecommendationsRefetch: () => void;
  reloadTimeline: () => void;
  /** The Alerts tab exists only for a user with the Use alerts right (PDW-182). */
  showAlerts: boolean;
  alertPanel: AlertEpisodePanelDto | undefined;
  alertPanelLoading: boolean;
  alertPanelError: boolean;
  alertPanelRefetch: () => void;
};
/** Whether the tab exists for this user: a link or a saved state may carry a tab the user does not have (Alerts without the Use alerts right). */
export function isRightTabAvailable(tab: number, showAlerts: boolean): boolean {
  if (tab === RIGHT_TAB_ALERTS) return showAlerts;
  if (tab === RIGHT_TAB_PH_RECOMMENDATION) return ENABLE_PH_REC;
  return tab === RIGHT_TAB_RISK_FACTORS;
}
export const HospitalizationRightPaneV2: React.FC<LightLayoutNodeFCProps & HospitalizationRightPaneProps> = (props) => {
  const {
    state,
    changeState,
    storageIdPrefix,
    config,
    hospitalizationId,
    defaultDepartmentId,
    riskFactorToRiskFactorLabel,
    patientCardLoading,
    riskFactors,
    riskFactorsEtag,
    oldRiskFactorIds,
    reloadRiskFactors,
    setTimelineDefaultScroll,
    minimized,
    maximized,
    setMinimizedMaximized,
    pharmaRecommendations,
    pharmaRecommendationsLoading,
    pharmaRecommendationsRefetch,
    reloadTimeline,
    showAlerts,
    alertPanel,
    alertPanelLoading,
    alertPanelError,
    alertPanelRefetch,
  } = props;
  const selectedTab = state.rightTabs ?? RIGHT_TAB_RISK_FACTORS;
  // every tab shows the count of its active items, visible while another tab is open (PDW-182)
  const riskFactorsCount = riskFactors.filter((v) => v.isActive).length;
  const phRecommendationsCount = pharmaRecommendations?.pharmaRecommendations?.length ?? 0;
  const alertsCount = alertPanel?.activeCount ?? 0;
  const renderAlertCriteria = useMerieAlertCriteriaRenderer(config);
  const setSelectedTab = (newValue: number) => changeState({rightTabs: newValue});
  // A link from the alerts list (PDW-405) opens the Alerts tab as a click on it would, the pane expanded, and hands the
  // episode to the panel, which brings it into view once loaded; the link's parameter is dropped from the URL right away
  const [focusedEpisodeId, setFocusedEpisodeId] = useState<number | undefined>(undefined);
  const linkedEpisodeId = state.alertItem?.focusedEpisodeId;
  useEffect(() => {
    if (linkedEpisodeId === undefined) return;
    setFocusedEpisodeId(linkedEpisodeId);
    setMinimizedMaximized({minimized: false});
    changeState({alertItem: undefined});
  }, [linkedEpisodeId, setMinimizedMaximized, changeState]);
  const clearFocusedEpisode = useCallback(() => setFocusedEpisodeId(undefined), []);
  const {t} = useFormatters();
  const rightTabs: LightFilterTabDef[] = [
    {
      id: String(RIGHT_TAB_RISK_FACTORS),
      label: t('hospitalization.detailV2.riskFactorsTab'),
      iconName: 'tabRiskFactorsIcon',
      count: riskFactorsCount,
    },
    ...(showAlerts
      ? [{id: String(RIGHT_TAB_ALERTS), label: t('hospitalization.detailV2.alertsTab'), iconName: 'tabAlertsIcon', count: alertsCount}]
      : []),
    ...(ENABLE_PH_REC
      ? [
          {
            id: String(RIGHT_TAB_PH_RECOMMENDATION),
            label: t('hospitalization.detailV2.phRecommendationTab'),
            iconName: 'tabPhRecommendationIcon',
            count: phRecommendationsCount,
          },
        ]
      : []),
  ];
  const isBigMode = !minimized || maximized;
  return (
    <div className='dl-light-box' data-page-box='true' data-fill-available={isBigMode}>
      <div className='dl-header-buttons-row-wide' data-rotate={isBigMode ? '' : 'right'}>
        {/* The design's tab strip (PDW-182): icon pills with a count badge, only the active tab carries its label, so every
            tab's count is visible while another tab is open. The ids are the fixed tab values. The collapsed pane's strip
            shows the icons only, on the same axis as the expand and maximize buttons: no counts, and no gap towards the
            header buttons, which in the stacked column would push the tabs off that axis. The open pane's tabs take the size
            of the left pane's Patient / Documents tabs, as tall as the header buttons (PDW-421); the collapsed strip keeps
            its own. */}
        <div style={{flex: 1, minWidth: 0, paddingRight: isBigMode ? 8 : 0}}>
          <LightFilterTabs
            tabs={isBigMode ? rightTabs : rightTabs.map((tab) => ({...tab, count: undefined}))}
            activeTab={String(selectedTab)}
            onChange={(tabId) => {
              setSelectedTab(Number(tabId));
              setMinimizedMaximized({minimized: false});
            }}
            compact
            showZeroCount={isBigMode}
            variant='panel'
            pageTabSize={isBigMode}
            dataName='hospitalization-right-tabs'
          />
        </div>
        <div className='dl-header-icons-row'>
          <MinimizeRightButton
            minimized={minimized}
            toggleMinimized={() => setMinimizedMaximized({minimized: !minimized, maximized: false})}
          />
          <MaximizeButton maximized={maximized} toggleMaximized={() => setMinimizedMaximized({maximized: !maximized})} tall />
        </div>
      </div>
      {isBigMode && (
        <>
          {selectedTab === RIGHT_TAB_ALERTS && showAlerts && (
            <div data-fill-available style={{height: 'calc(100% - var(--horizontal-tab-height))', overflowY: 'auto'}}>
              <AlertEpisodePanel
                config={config}
                storageIdPrefix={storageIdPrefix}
                panel={alertPanel}
                isLoading={alertPanelLoading}
                isError={alertPanelError}
                refetch={alertPanelRefetch}
                renderCriteria={renderAlertCriteria}
                focusedEpisodeId={focusedEpisodeId}
                onFocusedEpisodeShown={clearFocusedEpisode}
                getEpisodeHref={alertEpisodeHref}
              />
            </div>
          )}
          {selectedTab === RIGHT_TAB_RISK_FACTORS && (
            <HospitalizationRiskFactorsV2
              state={state}
              changeState={changeState}
              storageIdPrefix={storageIdPrefix}
              config={config}
              hospitalizationId={hospitalizationId}
              riskFactors={riskFactors}
              riskFactorsEtag={riskFactorsEtag}
              oldRiskFactorIds={oldRiskFactorIds}
              reloadRiskFactors={reloadRiskFactors}
              setTimelineDefaultScroll={setTimelineDefaultScroll}
              setMinimizedMaximized={setMinimizedMaximized}
            />
          )}
          {selectedTab === RIGHT_TAB_PH_RECOMMENDATION && ENABLE_PH_REC && (
            <HospitalizationPhRecommendationPanelV2
              state={state}
              changeState={changeState}
              storageIdPrefix={storageIdPrefix}
              config={config}
              hospitalizationId={hospitalizationId}
              defaultDepartmentId={defaultDepartmentId}
              riskFactorToRiskFactorLabel={riskFactorToRiskFactorLabel}
              patientCardLoading={patientCardLoading}
              encounterRiskFactors={riskFactors}
              pharmaRecommendationsLoading={pharmaRecommendationsLoading}
              pharmaRecommendations={pharmaRecommendations}
              pharmaRecommendationsRefetch={pharmaRecommendationsRefetch}
              reloadTimeline={reloadTimeline}
            />
          )}
        </>
      )}
    </div>
  );
};
